/**
 * Provenance header for the combined-drafts card popover: "Combines comments
 * from Jul 17 at 9:04pm and 10:15pm, and Jul 18 at 8:30am", built from the
 * absorbed_comment_timestamps on every combined action in the payload.
 * Timestamps sharing a calendar day (in the display zone) are grouped so the
 * day is stated once.
 */

import moment from 'moment-timezone'
import { userTimeZone } from '../userTimeZone'
import type { DraftAction } from '../../models/aiAnalysis/workflow'

// Matches the comment stamp's own format ("Jul 18, 2026 at 1:44pm CST") minus
// the year and zone abbreviation — the year is redundant this close to "now",
// and the zone parity displayTimeZone provides already makes these times line
// up with the stamps next to them, so no zone label needed.
const DAY_FORMAT = 'MMM D'
const TIME_FORMAT = 'h:mma'

// Union across every combined action's absorbed_comment_timestamps, dedupe,
// and sort ascending. Multiple combined actions in one payload (client +
// notary) can share source comments, so the union step matters even though
// each individual array already arrives deduped and sorted from the backend.
//
// Sort by parsed instant, not lexically: absorbed timestamps can carry mixed
// UTC offsets when a pile-up straddles a DST transition, and a default string
// sort of ISO8601 values with mixed offsets is not chronological ('...-08:00'
// sorts before '...-07:00' regardless of the true instant). Compare the parsed
// epoch millis so day-grouping downstream sees them in real time order.
export const collectAbsorbedCommentTimestamps = (actions: DraftAction[]): string[] => {
  const all = actions.reduce<string[]>(
    (acc, action) => acc.concat(action.action_payload.absorbed_comment_timestamps ?? []),
    []
  )
  return Array.from(new Set(all)).sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
}

const joinWithOxfordAnd = (items: string[]): string => {
  if (items.length === 1) return items[0]
  if (items.length === 2) return `${items[0]} and ${items[1]}`
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`
}

// Day groups keep a comma before the final "and" even when there are only two
// groups ("…8:11pm, and Jul 18 at 12:11pm") — each group already contains
// commas and an "and" of its own, so the comma is what marks the day boundary.
const joinDayGroups = (groups: string[]): string => {
  if (groups.length === 1) return groups[0]
  return `${groups.slice(0, -1).join(', ')}, and ${groups[groups.length - 1]}`
}

// displayTimeZone, when provided, is the IANA zone the admin comment thread
// itself renders timestamps in for this order (current_user_order_timezone
// in orders_helper.rb — the viewer's personal zone preference, or the
// order's zone as fallback). The header must match what the thread shows,
// not the viewer's raw browser zone, so this takes precedence over
// userTimeZone() whenever the backend supplied it. Grouping happens on the
// zone-converted day, so a pair straddling midnight groups per what the
// viewer actually sees.
const formatGrouped = (timestamps: string[], displayTimeZone?: string | null): string => {
  const zone = displayTimeZone || userTimeZone()
  const groups: { day: string; times: string[] }[] = []
  timestamps.forEach((isoTimestamp) => {
    const inZone = moment(isoTimestamp).tz(zone)
    const day = inZone.format(DAY_FORMAT)
    const time = inZone.format(TIME_FORMAT)
    const lastGroup = groups[groups.length - 1]
    if (lastGroup && lastGroup.day === day) {
      lastGroup.times.push(time)
    } else {
      groups.push({ day, times: [time] })
    }
  })
  return joinDayGroups(groups.map((group) => `${group.day} at ${joinWithOxfordAnd(group.times)}`))
}

// Returns null when there's nothing to show — either no combined action
// carried the field at all (older payload; no header, no badge fallback) or
// the union came back empty.
export const getProvenanceHeaderText = (actions: DraftAction[], displayTimeZone?: string | null): string | null => {
  const hasField = actions.some((action) => action.action_payload.absorbed_comment_timestamps != null)
  if (!hasField) return null

  const timestamps = collectAbsorbedCommentTimestamps(actions)
  if (timestamps.length === 0) return null

  const prefix = timestamps.length === 1 ? 'Combines a comment from' : 'Combines comments from'
  return `${prefix} ${formatGrouped(timestamps, displayTimeZone)}`
}
